import useReportSort from '../../components/reports/useReportSort';
import { useMemo, useState } from "react";
import {
  FiAlertCircle,
  FiChevronLeft,
  FiChevronRight,
  FiDownload,
  FiFileText,
  FiFilter,
  FiLoader,
  FiRefreshCw,
  FiShield,
  FiShoppingCart,
} from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";
import { hasPermission } from "../../utils/permissions";
import ReportFilterModal from "../../components/reports/ReportFilterModal";

const money = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const number = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 3,
  });

const dateOnly = (value) => {
  if (!value) return "—";

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(new Date(value));
};

const localDate = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate(),
  ).padStart(2, "0")}`;

function downloadFile(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function FilterField({ label, children }) {
  return (
    <label className="block text-sm font-semibold text-gray-700">
      {label}
      <div className="mt-2">{children}</div>
    </label>
  );
}

export default function SalesRegisterSummaryPage() {
  const { accessToken, user } = useAuth();
  const now = new Date();
  const initialFilters = {
    from: localDate(new Date(now.getFullYear(), now.getMonth(), 1)),
    to: localDate(now),
    search: "",
  };

  const [draftFilters, setDraftFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [rawReport, setReport] = useState(null);
  const { report, controls: sortControls } = useReportSort(rawReport, () => setPage(1));
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  const canGenerate = hasPermission(
    user,
    "reports.sales_register_summary.generate",
  );
  const canExport = hasPermission(
    user,
    "reports.sales_register_summary.export",
  );

  const rows = useMemo(() => {
    const term = appliedFilters.search.trim().toLowerCase();
    const allRows = report?.rows || [];

    if (!term) return allRows;

    return allRows.filter((row) =>
      [row.billNumber, row.customerName]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term)),
    );
  }, [appliedFilters.search, report]);

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const visibleRows = rows.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  const generate = async () => {
    if (!canGenerate) return;

    setLoading(true);
    setError("");

    try {
      const nextReport = await apiRequest(
        "/business-reports/sales-register-summary/generate",
        {
          method: "POST",
          body: {
            from: draftFilters.from,
            to: draftFilters.to,
          },
          accessToken,
        },
      );

      setReport(nextReport);
      setAppliedFilters(draftFilters);
      setPage(1);
      setShowFilters(false);
    } catch (requestError) {
      setReport(null);
      setError(
        requestError.message || "Could not generate the sales register.",
      );
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = async () => {
    if (!report || !canExport) return;

    setExporting(true);
    setError("");

    try {
      const response = await apiRequest(
        `/business-reports/sales-register-summary/export?${new URLSearchParams({
          from: appliedFilters.from,
          to: appliedFilters.to,
          format: "XLSX",
        })}`,
        { accessToken, rawResponse: true },
      );
      const contentDisposition = response.headers.get("content-disposition");
      const fileName =
        contentDisposition?.match(/filename="?([^";]+)"?/)?.[1] ||
        "charms-sales-register-summary.xlsx";

      downloadFile(await response.blob(), fileName);
    } catch (requestError) {
      setError(requestError.message || "Could not export the sales register.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="business-report space-y-6">
      <section className="rounded-3xl border border-primary-100 bg-white p-6 text-primary-800 shadow-sm lg:p-8">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.18em] text-black">
              <FiShoppingCart /> Business reports
            </div>
            <h1 className="text-3xl font-bold">Sales Register Summary</h1>
            <p className="mt-2 max-w-2xl text-black">
              One completed POS bill per row, with gross amount, total bill
              discount, final bill amount, packs, and customer name.
            </p>
          </div>
          <div className="rounded-2xl border border-white/20 bg-white/10 px-4 py-3 text-sm font-medium text-primary-50">
            Generate → preview → export Excel
          </div>
        </div>
      </section>

      <section className="rounded-3xl border border-gray-100 bg-white p-5 shadow-sm lg:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-primary-700">
              Report period
            </p>
            <h2 className="mt-1 text-xl font-bold text-gray-900">
              {dateOnly(appliedFilters.from)} – {dateOnly(appliedFilters.to)}
            </h2>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => setShowFilters(true)}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 transition hover:border-primary-200 hover:bg-primary-50"
            >
              <FiFilter /> Filters
            </button>
            <button
              type="button"
              onClick={generate}
              disabled={!canGenerate || loading}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary-800 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-900 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? (
                <FiLoader className="animate-spin" />
              ) : (
                <FiRefreshCw />
              )}
              {loading ? "Generating…" : "Generate report"}
            </button>
          </div>
        </div>
        {!canGenerate && (
          <p className="mt-4 flex items-center gap-2 text-sm text-amber-700">
            <FiShield /> You can view this report, but do not have permission to
            generate it.
          </p>
        )}
      </section>

      {error && (
        <div className="flex gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <FiAlertCircle className="mt-0.5 shrink-0" />
          {error}
        </div>
      )}

      {!report ? (
        <section className="rounded-3xl border border-dashed border-gray-300 bg-white px-6 py-16 text-center shadow-sm">
          <FiFileText className="mx-auto mb-4 text-4xl text-primary-300" />
          <h2 className="text-xl font-bold text-gray-900">
            No preview generated yet
          </h2>
          <p className="mt-2 text-sm text-gray-500">
            Select a period and generate the POS sales register before exporting
            it.
          </p>
        </section>
      ) : (
        <section className="overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm">
          <div className="flex flex-col gap-4 border-b border-gray-100 p-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Sale Register (Summary)
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                {rows.length.toLocaleString("en-IN")} of{" "}
                {report.summary.bills.toLocaleString("en-IN")} POS bill(s)
                shown.
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <button
                type="button"
                onClick={exportExcel}
                disabled={!canExport || exporting}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-primary-200 bg-white px-4 py-2.5 text-sm font-semibold text-primary-800 transition hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {exporting ? (
                  <FiLoader className="animate-spin" />
                ) : (
                  <FiDownload />
                )}
                {exporting ? "Preparing…" : "Export Excel"}
              </button>
            </div>
          </div>

          {!canExport && (
            <p className="mx-5 mt-5 flex items-center gap-2 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-700">
              <FiShield /> You can preview this register, but do not have export
              permission.
            </p>
          )}

          <div className="overflow-x-auto">
                  {sortControls}
            <table className="min-w-[1050px] w-full text-sm">
              <thead className="bg-primary-800 text-left text-xs font-bold uppercase tracking-wide text-white">
                <tr>
                  {[
                    "S. No.",
                    "Bill Date",
                    "Bill Number",
                    "Gross Amount",
                    "Tot. C.D.(%)",
                    "Bill Amount",
                    "Total Packs",
                    "Customer Name",
                  ].map((column) => (
                    <th key={column} className="whitespace-nowrap px-4 py-3.5">
                      {column}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row) => (
                  <tr
                    key={row.billNumber}
                    className="border-t border-gray-100 transition hover:bg-primary-50/40"
                  >
                    <td className="px-4 py-3 text-gray-500">
                      {row.serialNumber}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      {dateOnly(row.billDate)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 font-semibold text-primary-800">
                      {row.billNumber}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums">
                      {money(row.grossAmount)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right font-semibold tabular-nums text-red-600">
                      {Number(row.totalDiscount) > 0
                        ? `-${money(row.totalDiscount)}`
                        : "—"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right font-bold tabular-nums text-gray-900">
                      {money(row.billAmount)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums">
                      {number(row.totalPacks)}
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-800">
                      {row.customerName || "CASH CUSTOMER"}
                    </td>
                  </tr>
                ))}
                {!visibleRows.length && (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-6 py-14 text-center text-sm text-gray-500"
                    >
                      No POS bills match the generated period and search.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col gap-4 border-t border-gray-100 p-5 md:flex-row md:items-center md:justify-between">
            <p className="text-sm text-gray-500">
              Showing {rows.length ? (currentPage - 1) * pageSize + 1 : 0}–
              {Math.min(currentPage * pageSize, rows.length)} of {rows.length}{" "}
              bill(s)
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-600">
                Rows
                <select
                  value={pageSize}
                  onChange={(event) => {
                    setPageSize(Number(event.target.value));
                    setPage(1);
                  }}
                  className="rounded-lg border border-gray-200 bg-white px-2.5 py-2 text-sm outline-none focus:border-primary-700"
                >
                  {[25, 50, 100, 200, 300].map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                onClick={() => setPage((current) => Math.max(1, current - 1))}
                disabled={currentPage === 1}
                className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <FiChevronLeft /> Previous
              </button>
              <span className="text-sm font-semibold text-gray-700">
                {currentPage} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() =>
                  setPage((current) => Math.min(totalPages, current + 1))
                }
                disabled={currentPage === totalPages}
                className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next <FiChevronRight />
              </button>
            </div>
          </div>
        </section>
      )}

      <ReportFilterModal
        open={showFilters}
        title="Sales Register Summary Filters"
        description="Choose the POS bill period. Search filters the generated register by bill number or customer."
        loading={loading}
        onClose={() => setShowFilters(false)}
        onApply={generate}
      >
        <div className="grid gap-5 sm:grid-cols-2">
          <FilterField label="From date">
            <input
              type="date"
              value={draftFilters.from}
              onChange={(event) =>
                setDraftFilters((current) => ({
                  ...current,
                  from: event.target.value,
                }))
              }
              className="w-full rounded-xl border border-gray-200 px-3 py-2.5 outline-none focus:border-primary-700 focus:ring-2 focus:ring-primary-100"
            />
          </FilterField>
          <FilterField label="To date">
            <input
              type="date"
              value={draftFilters.to}
              onChange={(event) =>
                setDraftFilters((current) => ({
                  ...current,
                  to: event.target.value,
                }))
              }
              className="w-full rounded-xl border border-gray-200 px-3 py-2.5 outline-none focus:border-primary-700 focus:ring-2 focus:ring-primary-100"
            />
          </FilterField>
        </div>
        <div className="mt-5">
          <FilterField label="Search in generated results">
            <input
              value={draftFilters.search}
              onChange={(event) =>
                setDraftFilters((current) => ({
                  ...current,
                  search: event.target.value,
                }))
              }
              placeholder="Bill number or customer name"
              className="w-full rounded-xl border border-gray-200 px-3 py-2.5 outline-none focus:border-primary-700 focus:ring-2 focus:ring-primary-100"
            />
          </FilterField>
        </div>
      </ReportFilterModal>
    </div>
  );
}
